"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.mapBasketItemsToSelectionState = exports.default = exports.Component = void 0;
var _hopsSdkJs = require("@hps/hops-sdk-js");
var _moment = _interopRequireDefault(require("moment"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _react = _interopRequireWildcard(require("react"));
var _sprintfJs = require("sprintf-js");
var _SeatReservationTrain = _interopRequireDefault(require("./SeatReservationTrain.js"));
var _Loadable = _interopRequireDefault(require("../Components/Loadable.js"));
var _Paper = _interopRequireDefault(require("../Components/Paper.js"));
var _TabPanel = _interopRequireDefault(require("../Components/TabPanel.js"));
var _SeatReservationCheckoutUiModule = _interopRequireDefault(require("./SeatReservationCheckoutUi.module.scss"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
/**
 * Seat reservation checkout UI
 *
 * Renders the seat reservation selection for 
 * the set of trains forming a ticketed journey.
 *
 * To use - you must set **either** `targetBasketItem`, 
 * **or** `targetJourneys`, `reservationMode`, and `targetSeatCount`:
 * 	- `targetBasketItem` - The single `CheckoutBasketItem`-like object 
 * 		to render for.
 * 	- `targetJourneys` - An array of journey objects to render for.
 * 		Each journey object must have the structure:
 * 			{
 *				Train: object{
 *					Id: int
 *				},
 *				DepartureTrainSchedule: object{
 *					Id: int,
 *					DepartureTime: string			// HH:MM:SS,
 *					TimingPoint: object{
 *						Name: string
 *					}
 *				},
 *				ArrivalTrainSchedule: object{
 *					Id: int,
 *					DepartureTime: string			// HH:MM:SS,
 *					TimingPoint: object{
 *						Name: string
 *					}
 *				}
 *			}
 *	- `reservationMode` - Integer seat reservation mode enum value
 *	- `targetSeatCount` - Maximum number of seats permitted to be selected
 * 
 * To control the user's seat selection, set the `selection` prop as an 
 * object where each property should be a journey ID (i.e. from 
 * `SeatReservationUtils.getJourneyId()`) and each value should 
 * be an array<int> containing the IDs of the spaces selected 
 * for that journey.
 * 
 * @package HOPS
 * @subpackage SeatReservation
 * @author Heron Web Ltd
 * @copyright Heritage Operations Processing Limited
 */const SeatReservationCheckoutUi = exports.Component = /*#__PURE__*/(0, _react.memo)(props => {
  const {
    onAddSelection,
    onRemoveSelection,
    onFetchedSeatingData,
    containerLevel,
    initialSelection,
    knownReservations,
    reservationMode,
    selection,
    targetBasketId,
    targetBasketItem,
    targetOrderId,
    targetJourneys,
    targetSeatCount
  } = props;
  const [data, setData] = (0, _react.useState)(null);
  const [error, setError] = (0, _react.useState)(null);
  const [loading, setLoading] = (0, _react.useState)(true);
  const [activeJourneyTab, setActiveJourneyTab] = (0, _react.useState)(0);

  /**
   * This is required for filtering out reservations 
   * that are actually made by us (see below)
   *
   * @type {ReactRef}
   */
  const fetchedDataRef = (0, _react.useRef)(null);

  /**
   * Selected space reservations
   *
   * @type {Object} Journey ID => Array of selected space IDs
   */
  const [selectionState, setSelectionState] = (0, _react.useState)(initialSelection || {});

  /**
   * Active selection object
   */
  const activeSelection = selection || selectionState;
  const isControlledSelection = !!selection;

  /**
   * Get the journeys we're travelling on
   */
  const journeys = (0, _react.useMemo)(() => {
    return targetJourneys ?? (targetBasketItem ? _hopsSdkJs.CheckoutBasketItem.getJourneys(targetBasketItem) : []);
  }, [targetBasketItem, targetJourneys]);

  /**
   * Number of seats we want (per journey)
   */
  const seatCount = (0, _react.useMemo)(() => {
    return targetSeatCount ?? _hopsSdkJs.SeatReservationUtils.getSeatCountForBasketItem(targetBasketItem, false);
  }, [targetBasketItem, targetSeatCount]);

  /**
   * Are we required to select our number of seats, or merely allowed?
   */
  const activeReservationMode = reservationMode ?? _hopsSdkJs.CheckoutBasketItem.getSeatReservationMode(targetBasketItem);
  const isSelectionRequired = activeReservationMode === _hopsSdkJs.TicketSeatReservationModes.values.CustomerSelectionMandatory;

  /**
   * Get whether a reservation is within our selection.
   * 
   * @param {String} journeyId See `SeatReservationUtils`
   * @param {Integer} spaceId
   * @param {Object} reservations optional Reservations object to compare to (see `selectionState`)
   * @return {Boolean}
   */
  const isReserved = (0, _react.useCallback)((journeyId, spaceId, reservations = null) => {
    return ((reservations || activeSelection)?.[journeyId] || []).includes(spaceId);
  }, [activeSelection]);

  /**
   * Add a reservation to the selection.
   *
   * This has to use the functional form of `setState()`, due to 
   * the way in which it's used in `handleSelectionChange()` below.
   *
   * @param {String} journeyId See `SeatReservationUtils`
   * @param {Integer} spaceId
   * @return {void}
   */
  const addToSelection = (0, _react.useCallback)((journeyId, spaceId) => {
    setSelectionState(selection => {
      const updatedSelection = {
        ...selection
      };
      if (!isReserved(journeyId, spaceId, selection)) {
        updatedSelection[journeyId] ||= [];
        updatedSelection[journeyId].push(spaceId);
      }
      return updatedSelection;
    });
  }, [isReserved]);

  /**
   * Remove a reservation from the selection.
   * 
   * This has to use the functional form of `setState()`, due to 
   * the way in which it's used in `handleSelectionChange()` below.
   *
   * @param {String} journeyId See `SeatReservationUtils`
   * @param {Integer} spaceId
   * @return {void}
   */
  const removeFromSelection = (0, _react.useCallback)((journeyId, spaceId) => {
    setSelectionState(selection => {
      return {
        ...selection,
        [journeyId]: (selection[journeyId] || []).filter(s => s !== spaceId)
      };
    });
  }, []);

  /**
   * Load our seating data.
   *
   * @return {void}
   */
  const getData = (0, _react.useCallback)(async () => {
    setData(null);
    setError(null);

    /**
     * Must be a valid item
     */
    if (!journeys?.length || !(_hopsSdkJs.TicketSeatReservationModes.allowsCustomerSelection(activeReservationMode) || _hopsSdkJs.TicketSeatReservationModes.allowsRailwaySelection(activeReservationMode))) {
      setLoading(false);
      setError(new Error("Seating Configuration Invalid"));
      return;
    }

    /**
     * Establish the API search data now
     */
    const searchData = journeys.map(journey => {
      return {
        Train: journey?.Train?.Id,
        TargetDepartureTrainSchedule: journey?.DepartureTrainSchedule?.Id,
        TargetArrivalTrainSchedule: journey?.ArrivalTrainSchedule?.Id
      };
    });

    /**
     * Can we search?
     */
    if (!searchData) return;
    setLoading(true);

    /**
     * Search!
     */
    try {
      const data = await _hopsSdkJs.TicketService.searchSeats({
        Trains: searchData
      });
      setData(data);
      onFetchedSeatingData?.(data);
      fetchedDataRef.current = data;
    } catch (e) {
      setError(e);
    }
    setLoading(false);
  }, [activeReservationMode, journeys]);

  /**
   * Reservation selection changed.
   *
   * @param {Object} train
   * @param {Object} trainAsset
   * @param {?Integer} trainScheduleIdDeparture
   * @param {?Integer} trainScheduleIdArrival
   * @param {Object} space
   * @return {void}
   */
  const handleSelectionChange = (0, _react.useCallback)(async (train, trainAsset, trainScheduleIdDeparture, trainScheduleIdArrival, space) => {
    let error = false;
    const journeyId = _hopsSdkJs.SeatReservationUtils.getJourneyId(trainAsset.Id, trainScheduleIdDeparture, trainScheduleIdArrival);
    const spaceId = space.Id;

    /**
     * We are selecting the seat
     */
    if (!isReserved(journeyId, spaceId)) {
      if (!isControlledSelection) addToSelection(journeyId, spaceId);
      try {
        const result = await onAddSelection(train, trainAsset, trainScheduleIdDeparture, trainScheduleIdArrival, space, targetBasketItem);
        error = !result || result instanceof Error || result instanceof _hopsSdkJs.ItemClaimErrorCodes.ItemClaimErrorCode;
        if (result instanceof _hopsSdkJs.ItemClaimErrorCodes.ItemClaimErrorCode) {
          if (result.code === _hopsSdkJs.ItemClaimErrorCodes.default.ItemUnfulfilable) {
            const journeyId = _hopsSdkJs.SeatReservationUtils.getTrainJourneyId(train.Id, trainScheduleIdDeparture, trainScheduleIdArrival);
            setData(data => ({
              ...data,
              SeatReservations: {
                ...data.SeatReservations,
                [journeyId]: [...data.SeatReservations[journeyId], {
                  TrainAsset: trainAsset.Id,
                  DepartureTrainSchedule: trainScheduleIdDeparture,
                  ArrivalTrainSchedule: trainScheduleIdArrival,
                  Space: spaceId
                }]
              }
            }));
          }
        }
      } catch (e) {
        error = e;
      }
      if (error && !isControlledSelection) removeFromSelection(journeyId, spaceId);
    }

    /**
     * We are de-selecting the seat
     */else {
      if (!isControlledSelection) removeFromSelection(journeyId, spaceId);
      try {
        const result = await onRemoveSelection(trainAsset.Id, trainScheduleIdDeparture, trainScheduleIdArrival, spaceId);
        error = !result || result instanceof Error;
      } catch (e) {
        error = e;
      }
      if (error && !isControlledSelection) addToSelection(journeyId, spaceId);
    }
  }, [addToSelection, isControlledSelection, isReserved, onAddSelection, onRemoveSelection, removeFromSelection, targetBasketItem]);
  const getJourneyTabs = () => {
    const tabs = journeys.map((journey, key) => {
      const index = key + 1;
      const journeyId = _hopsSdkJs.SeatReservationUtils.getTrainJourneyId(journey.Train?.Id, journey.DepartureTrainSchedule?.Id, journey.ArrivalTrainSchedule?.Id);
      const train = data?.Trains?.find(train => train.Id === journey.Train.Id);
      if (!train) return null;
      let selectedSpaces = [];
      let trainCaption = new _moment.default(train.Date).format("MMMM DD YYYY");
      let trainJourneyCaption = train.Time;

      /**
       * Get the assets and seat plans for this traiin
       */
      const assets = train.Assets.map(trainAsset => ({
        ...trainAsset,
        Asset: data.Assets?.find(a => a.Id === trainAsset.Asset)
      }));
      const assetSeatPlans = assets.map(a => a.Asset.SeatPlan);
      const seatPlans = data.SeatPlans.filter(sp => assetSeatPlans.includes(sp.Id));

      /**
       * Discover our spaces booked on the train
       */
      for (const asset of assets) {
        selectedSpaces = [...selectedSpaces, ...(activeSelection?.[_hopsSdkJs.SeatReservationUtils.getJourneyId(asset.Id, journey.DepartureTrainSchedule?.Id, journey.ArrivalTrainSchedule?.Id)] || [])];
      }

      /**
       * Produce the label for our journeys on this train
       */
      if (journey.DepartureTrainSchedule && journey.ArrivalTrainSchedule) {
        const journeyCaption = (0, _sprintfJs.sprintf)("%s %s - %s %s", journey?.DepartureTrainSchedule?.DepartureTime?.substring?.(0, 5), journey?.DepartureTrainSchedule?.TimingPoint?.Name, journey?.ArrivalTrainSchedule?.ArrivalTime?.substring?.(0, 5), journey?.ArrivalTrainSchedule?.TimingPoint?.Name);
        if (journeyCaption) {
          trainJourneyCaption = journeyCaption;
        }
      }
      trainCaption += `: ${trainJourneyCaption}`;
      return {
        label: `Journey ${index}`,
        hidden: false,
        component: _SeatReservationTrain.default,
        componentProps: {
          assets,
          caption: trainCaption,
          containerLevel,
          journeyId,
          index,
          maxSelectedCount: seatCount,
          onChangeSelection: handleSelectionChange,
          requiredSelectedCount: isSelectionRequired ? seatCount : 0,
          seatPlans,
          seatReservations: [...(data?.SeatReservations?.[journeyId] || []), ...(knownReservations || [])],
          selectedSpaces,
          showHeadcode: props.showHeadcode,
          targetBasketId,
          targetOrderId,
          train,
          trainScheduleIdArrival: journey.ArrivalTrainSchedule?.Id,
          trainScheduleIdDeparture: journey.DepartureTrainSchedule?.Id
        }
      };
    });
    return tabs;
  };
  const allJourneyTabs = getJourneyTabs();

  /**
   * Refresh data when the target basket item changes
   */
  (0, _react.useEffect)(getData, [getData]);

  /*
   * We filter out seat reservations that are actually 
   * selected by us (e.g. if we have seats selected, but 
   * are loading seat reservation a second time, the server 
   * data will indicate "our" seats are booked by another, 
   * so if the user then cleared one of those seats, they 
   * couldn't book it again as it would immediately show 
   * as booked by another user.)
   */
  (0, _react.useEffect)(() => {
    if (data && data === fetchedDataRef.current) {
      fetchedDataRef.current = null;
      const SeatReservations = {
        ...data?.SeatReservations
      };
      for (const journeyId of Object.keys(SeatReservations)) {
        SeatReservations[journeyId] = SeatReservations[journeyId]?.filter?.(sr => {
          return !isReserved(_hopsSdkJs.SeatReservationUtils.getJourneyId(sr.TrainAsset, sr.DepartureTrainSchedule, sr.ArrivalTrainSchedule), sr.Space);
        });
      }
      setData({
        ...data,
        SeatReservations
      });
    }
  }, [data, isReserved]);

  /**
   * Render!
   */
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Loadable.default, {
    error: !!error,
    errorStr: `Unable to load seating data: ${error}`,
    loading: loading,
    onRetry: getData,
    children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_Paper.default, {
      className: _SeatReservationCheckoutUiModule.default.paper,
      variant: "outlined",
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_TabPanel.default, {
        gap: 1,
        onChange: setActiveJourneyTab,
        tabs: allJourneyTabs,
        scrollButtons: "auto",
        value: activeJourneyTab,
        variant: "scrollable"
      })
    })
  });
});

/**
 * Prop types.
 */
SeatReservationCheckoutUi.propTypes = {
  onAddSelection: _propTypes.default.func,
  onRemoveSelection: _propTypes.default.func,
  onFetchedSeatingData: _propTypes.default.func,
  containerLevel: _propTypes.default.number,
  initialSelection: _propTypes.default.object,
  knownReservations: _propTypes.default.arrayOf(_propTypes.default.object),
  reservationMode: _propTypes.default.number,
  showHeadcode: _propTypes.default.bool,
  targetBasketItem: _propTypes.default.object,
  targetJourneys: _propTypes.default.arrayOf(_propTypes.default.object),
  targetSeatCount: _propTypes.default.number
};

/**
 * Default props.
 */
SeatReservationCheckoutUi.defaultProps = {
  containerLevel: 0,
  knownReservations: [],
  initialSelection: {},
  showHeadcode: true
};

/**
 * Map a set of basket items to create a `selection` state object 
 * that's ready to be consumed by the component.
 *
 * @param {Array<Object>} basketItems CheckoutBasketItem-like objects
 * @return {Object} See `selection` in `SeatReservationCheckoutUi`
 */
const mapBasketItemsToSelectionState = basketItems => {
  const selection = {};
  basketItems.filter(i => i.OrderableType === _hopsSdkJs.OrderableTypes.SeatReservation).forEach(i => {
    const journeyId = _hopsSdkJs.SeatReservationUtils.getJourneyId(i.Item.TrainAsset.Id, i.Item.DepartureTrainSchedule, i.Item.ArrivalTrainSchedule);
    selection[journeyId] ||= [];
    selection[journeyId].push(i.Item.Space.Id);
  });
  return selection;
};
exports.mapBasketItemsToSelectionState = mapBasketItemsToSelectionState;
var _default = exports.default = SeatReservationCheckoutUi;